import axios from 'axios'
import { API_BASE_URL } from '@/services/config'

/** localStorage key the login token is stored under (see setAuthToken). */
export const TOKEN_KEY = 'mcsms.token'

/**
 * Serialise query params the way FastAPI's `List[str] = Query([])` expects:
 * an array becomes the same key repeated (`authority=A&authority=B`), not
 * axios's default bracket-suffixed `authority[]=A&authority[]=B` (which
 * Starlette treats as an unrelated param name and silently ignores).
 */
function paramsSerializer(params: Record<string, unknown>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue
    if (Array.isArray(value)) value.forEach((v) => search.append(key, String(v)))
    else search.append(key, String(value))
  }
  return search.toString()
}

/**
 * Shared axios client for the real backend (used when VITE_DATA_SOURCE=api).
 * Every request carries the login token (JWT) if one is present, so the
 * backend can identify the caller.
 */
export const httpClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer,
  // withCredentials: true, // enable if the backend uses httpOnly cookie auth
})

// Attach "Authorization: Bearer <token>" from localStorage on every request.
httpClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// If the token is rejected (expired/invalid), clear it so the app can re-auth.
httpClient.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) localStorage.removeItem(TOKEN_KEY)
    return Promise.reject(error)
  },
)
